import React, { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { profileApi } from '../services/profileApi';

interface Props {
  onSuccess: () => void;
  onSkip: () => void;
}

const CVUploadModal: React.FC<Props> = ({ onSuccess, onSkip }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, or DOCX file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be under 5MB.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const result = await profileApi.uploadCV(file);
      if (result.success) {
        onSuccess();
      } else {
        setError(result.error || 'Upload failed. Please try again.');
      }
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-jobesta-gray-90">Upload Your CV</h2>
          <button
            onClick={onSkip}
            className="text-jobesta-gray-60 hover:text-jobesta-gray-80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-jobesta-gray-70 mb-6">
          To load this job, please upload your CV so we can match it to the position.
        </p>

        {error && (
          <p className="text-sm text-red-500 mb-4">{error}</p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-jobesta-blue text-white text-sm font-medium hover:bg-opacity-90 transition-colors disabled:opacity-60"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Uploading...' : 'Choose CV File'}
          </button>
          <button
            onClick={onSkip}
            disabled={uploading}
            className="flex-1 px-4 py-2.5 rounded-lg border border-jobesta-gray-40 text-jobesta-gray-70 text-sm font-medium hover:bg-jobesta-gray-30 transition-colors disabled:opacity-60"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
};

export default CVUploadModal;
